import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Fieldset, Button, TextInput } from '@gsa/afp-component-library';
import { useBidLineDetails } from '../../provider/bid-line-details-provider';
import {
  AUTO_SAVE_BID_LINE_CLARIFICATION,
  REMOVE_BID_LINE_CLARIFICATION,
} from '../../provider/queries';
import { getHighlightClass } from '../../provider/helpers';

const Clarifications = ({ clarificationType }) => {
  const {
    bidLine,
    findHighlight,
    removeHighlight,
    bidDetailData,
    updateClarifications,
    isAdmin,
  } = useBidLineDetails();

  const existingClarifications = useMemo(() => {
    const cs = bidDetailData.clarifications.filter(
      (c) => c.clarificationType === clarificationType,
    );
    return structuredClone(cs); // deep clone to avoid mutating the original data when updating textareas
  }, [bidDetailData.clarifications, clarificationType]);
  const [textareas, setTextareas] = useState(existingClarifications);

  const setTextareValue = (index, value) => {
    const newTextareas = [...textareas];
    newTextareas[index].clarification = value;
    setTextareas(newTextareas);
  };

  const [autoSaveBidLineClarification] = useMutation(
    AUTO_SAVE_BID_LINE_CLARIFICATION,
  );
  const [removeBidLineClarification] = useMutation(
    REMOVE_BID_LINE_CLARIFICATION,
  );

  const addTextarea = () => {
    if (isAdmin) return; // skip API call for site admin, CS, CO, engineer
    const input = {
      bidLineId: bidLine.id,
      clarification: '',
      clarificationType,
    };
    autoSaveBidLineClarification({ variables: { input } }).then(
      ({ data: { autoSaveBidLineClarification: id } }) => {
        const newClr = { id, ...input };
        updateClarifications(newClr);
        setTextareas((prev) => [...prev, structuredClone(newClr)]);
      },
    );
  };

  const removeClarification = (id) => {
    if (isAdmin) return; // skip API call for site admin, CS, CO, engineer
    removeBidLineClarification({
      variables: {
        removeBidLineClarificationId: id,
      },
    }).then(() => {
      updateClarifications({ id }, true); // isDelete = true
      setTextareas((prev) => prev.filter((x) => x.id !== id));
    });
  };

  const updateClarification = async (id, value) => {
    if (isAdmin) return; // skip API call for site admin, CS, CO, engineer
    const updatedClarification = {
      id,
      bidLineId: bidLine.id,
      clarification: value,
      clarificationType,
    };
    autoSaveBidLineClarification({
      variables: { input: updatedClarification },
    }).then(() => {
      updateClarifications(updatedClarification);
    });
  };

  return (
    <Fieldset
      name="clarification"
      data-testid={
        clarificationType === 'C' ? 'clarifications' : 'technical-exceptions'
      }
    >
      <div className="margin-bottom-1">
        {clarificationType === 'C'
          ? 'Provide clarification for this bid line'
          : 'Provide explanation of any non-compliance for this bid line'}
      </div>
      <>
        {textareas.map((item, index) => {
          const highlight = findHighlight(
            'detail',
            'bid_line_clarification',
            item.id,
            'clarification',
          );
          const isClarrificationHighlighted = !!highlight;

          return (
            <div className="grid-row flex-row" key={item.id}>
              <div className="grid-col-4">
                <TextInput
                  inputClass={getHighlightClass(isClarrificationHighlighted)}
                  id={index}
                  value={item.clarification}
                  data-testid={
                    clarificationType === 'C'
                      ? `clarifications-input-${index}`
                      : `technical-exceptions-input-${index}`
                  }
                  type="textarea"
                  characterLimit={2000}
                  onChange={(e) => setTextareValue(index, e.target.value)}
                  onBlur={(event) => {
                    const { value } = event.target;
                    updateClarification(item.id, value);
                    if (isClarrificationHighlighted) {
                      removeHighlight(highlight.id);
                    }
                  }}
                />
              </div>
              <div className="grid-col-8 flex-align-start">
                <Button
                  variant="outline"
                  onClick={() => removeClarification(item.id)}
                  leftIcon={{ name: 'close' }}
                  style={{
                    height: 50,
                    width: 50,
                    margin: '134px 0 0 8px',
                    padding: 0,
                  }}
                  size="medium"
                  data-testid={
                    clarificationType === 'C'
                      ? `clarifications-${index}-remove-btn`
                      : `technical-exceptions-${index}-remove-btn`
                  }
                />
              </div>
            </div>
          );
        })}
        <Button
          variant="unstyled"
          size="medium"
          label={`Add ${
            clarificationType === 'C' ? 'clarification' : 'technical exception'
          }`}
          leftIcon={{ name: 'add' }}
          onClick={addTextarea}
          className="margin-top-1"
          data-testid={
            clarificationType === 'C'
              ? 'add-clarification'
              : 'add-technical-exception'
          }
        />
      </>
    </Fieldset>
  );
};

Clarifications.propTypes = {
  clarificationType: PropTypes.oneOf(['C', 'E']),
};

Clarifications.defaultProps = {
  clarificationType: 'C',
};

export default Clarifications;
